import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Filter, RefreshCw, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const CAT_LABELS = {
  bug: { label: "Fehler", color: "#EF4444" },
  improvement: { label: "Verbesserung", color: "#00E575" },
  question: { label: "Frage", color: "#3B82F6" },
  praise: { label: "Lob", color: "#A855F7" },
  other: { label: "Sonstiges", color: "#71717A" },
};

const SEV_LABELS = {
  low: { label: "Niedrig", color: "#71717A" },
  medium: { label: "Mittel", color: "#F59E0B" },
  high: { label: "Hoch", color: "#EF4444" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "Neu", color: "#3B82F6" },
  { value: "in_review", label: "In Prüfung", color: "#F59E0B" },
  { value: "planned", label: "Eingeplant", color: "#A855F7" },
  { value: "fixed", label: "Erledigt", color: "#00E575" },
  { value: "wont_fix", label: "Verworfen", color: "#71717A" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
};

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { params: { status: filter } } : {};
      const r = await api.get("/feedback", params);
      setItems(r.data);
    } catch (e) {
      toast.error("Konnte Feedback nicht laden");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateStatus = async (id, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.patch(`/feedback/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Status aktualisiert");
      fetchItems();
    } catch (e) {
      toast.error("Update fehlgeschlagen");
    }
  };

  const counts = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          data-testid="admin-feedback-back-btn"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 mb-4"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold">Beta-Tester Feedback</h1>
            <p className="text-sm text-zinc-400 mt-1">{items.length} Einträge {filter ? `(Filter: ${filter})` : ""}</p>
          </div>
          <Button
            data-testid="admin-feedback-refresh-btn"
            onClick={fetchItems}
            variant="ghost"
            className="text-zinc-300 hover:text-[#00E575]"
          >
            <RefreshCw size={16} className="mr-2" /> Neu laden
          </Button>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            data-testid="admin-feedback-filter-all"
            onClick={() => setFilter("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filter === "" ? "border-[#00E575] bg-[#00E575]/10 text-[#00E575]" : "border-zinc-700 bg-[#18181B] text-zinc-300"}`}
          >
            <Filter size={12} className="inline mr-1" />
            Alle ({items.length})
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              data-testid={`admin-feedback-filter-${s.value}`}
              onClick={() => setFilter(s.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filter === s.value ? "border-[#00E575] bg-[#00E575]/10 text-[#00E575]" : "border-zinc-700 bg-[#18181B] text-zinc-300"}`}
            >
              <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: s.color }} />
              {s.label} ({counts[s.value] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="animate-spin mr-2" /> Lade Feedback…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-800 rounded-xl">
            Noch kein Feedback {filter ? "mit diesem Status" : "vorhanden"}.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const isOpen = expanded === it.id;
              const cat = CAT_LABELS[it.category] || CAT_LABELS.other;
              const sev = SEV_LABELS[it.severity] || SEV_LABELS.medium;
              const st = STATUS_OPTIONS.find((s) => s.value === it.status) || STATUS_OPTIONS[0];
              return (
                <div
                  key={it.id}
                  data-testid={`admin-feedback-card-${it.id}`}
                  className="bg-[#18181B] border border-zinc-800 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : it.id)}
                    className="w-full text-left p-4 hover:bg-zinc-900/40 transition"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider" style={{ background: `${cat.color}20`, color: cat.color }}>
                            {cat.label}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider" style={{ background: `${sev.color}20`, color: sev.color }}>
                            {sev.label}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider" style={{ background: `${st.color}20`, color: st.color }}>
                            {st.label}
                          </span>
                        </div>
                        <p className="font-medium text-zinc-100 truncate">{it.title}</p>
                        <p className="text-xs text-zinc-300 mt-1">
                          {it.submitter_name || "Unbekannt"} · {it.submitter_role} · {fmtDate(it.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-zinc-800 p-4 space-y-4">
                      {it.description && (
                        <div>
                          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">Beschreibung</p>
                          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{it.description}</p>
                        </div>
                      )}

                      {it.screenshot_path && (
                        <div>
                          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Screenshot</p>
                          <a
                            href={fileUrl(it.screenshot_path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-lg overflow-hidden border border-zinc-700 hover:border-[#00E575]/50 transition"
                          >
                            <img src={fileUrl(it.screenshot_path)} alt="Screenshot" className="w-full max-h-80 object-contain bg-[#09090B]" />
                          </a>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {it.page_url && (
                          <div>
                            <p className="font-semibold text-zinc-400 uppercase tracking-wider mb-1">Seite</p>
                            <p className="text-zinc-300 font-mono break-all">{it.page_url}</p>
                          </div>
                        )}
                        {it.user_agent && (
                          <div>
                            <p className="font-semibold text-zinc-400 uppercase tracking-wider mb-1">Browser</p>
                            <p className="text-zinc-300 break-all">{it.user_agent}</p>
                          </div>
                        )}
                        {it.submitter_phone && (
                          <div>
                            <p className="font-semibold text-zinc-400 uppercase tracking-wider mb-1">Kontakt</p>
                            <p className="text-zinc-300 font-mono">{it.submitter_phone}</p>
                          </div>
                        )}
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Status ändern</p>
                        <div className="flex flex-wrap gap-2">
                          {STATUS_OPTIONS.map((s) => (
                            <button
                              key={s.value}
                              data-testid={`admin-feedback-status-${it.id}-${s.value}`}
                              onClick={() => updateStatus(it.id, s.value)}
                              disabled={it.status === s.value}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-zinc-700 bg-[#09090B] text-zinc-300 hover:border-[#00E575] hover:text-[#00E575] disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: s.color }} />
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
